import { IrrigationSystem } from "@/types/irrigationSystem";
import { FertilizerProduct } from "@/types/products";
import {
    object,
    date,
    number,
    string,
    array,
    boolean,
    InferType,
    mixed,
} from "yup";
import {
    depthToSpeed,
    productRateToTargetRateN,
    speedToDepth,
    targetRateNToProdutcRate,
} from "../utils/operations";
import { ApplicationMethod } from "@/types/enums";
import { isNumber } from "../utils/numbers";

export const fileInputSchema = array().of(
    object().shape({
        required: boolean().nullable(),
        file_label: string().when("required", {
            is: (required: boolean) => required,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
        }),
        unit: string().when("required", {
            is: (required: boolean) => required,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
        }),
        label: string().when("required", {
            is: (required: boolean) => required,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
        }),
        unit_options: array().of(string()),
    }),
);

export const skipTableValidationField = boolean().optional().default(false);

function applicationTableSchema() {
    return array().when("method", {
        is: (method: unknown) => method === ApplicationMethod.Fertigation,
        then: () =>
            array().of(
                object().shape({
                    target_rate_n: number()
                        .min(0)
                        .required("Required")
                        .label("Target Rate")
                        .typeError("Required"),
                    depth: number()
                        .typeError("Required")
                        .min(0)
                        .required("Required")
                        .label("Depth"),
                    product_rate: number()
                        .min(0)
                        .typeError("Required")
                        .required("Required")
                        .label("Product Rate"),
                }),
            ),
        otherwise: () =>
            array().of(
                object().shape({
                    target_rate_n: number()
                        .min(0)
                        .typeError("Required++")
                        .required("Required")
                        .label("Target Rate"),
                    product_rate: number()
                        .min(0)
                        .typeError("Required++")
                        .required("Required")
                        .label("Target Rate"),
                }),
            ),
    });
}

export const operationFormSchema = object().shape({
    _skipTableValidation: skipTableValidationField,
    start_date: date().required().label("Start Date"),
    prescription: number().nullable(),
    product: number().required().label("Product"),
    is_complete: boolean().required().label("Status"),
    note: string().nullable().label("Note"),
    method: string()
        .required()
        .label("Application Method")
        .oneOf([
            ApplicationMethod.InjectedKnifedCoultered,
            ApplicationMethod.Dribble,
            ApplicationMethod.Broadcast,
            ApplicationMethod.Topdress,
            ApplicationMethod.YDrop,
            ApplicationMethod.BroadcastTillage,
            ApplicationMethod.TwoXTwo,
            ApplicationMethod.Furrow,
            ApplicationMethod.Fertigation,
        ]),
    batch_fill: mixed(),
    pump: number().label("Pump"),
    template: number().nullable().notRequired(),
    properties: fileInputSchema,
    table: applicationTableSchema().when("_skipTableValidation", {
        is: (skip: boolean) => !!skip,
        then: () => array().notRequired().nullable(),
    }),
});

export function degreesError({
    table,
    method,
    irrigationSystem,
    hasSectors,
}: {
    table: OperationFormType["table"];
    method: OperationFormType["method"];
    irrigationSystem?: IrrigationSystem;
    hasSectors: boolean;
}) {
    if (
        method !== ApplicationMethod.Fertigation ||
        !irrigationSystem ||
        hasSectors ||
        !table
    ) {
        return undefined;
    }

    const pStart = irrigationSystem.pivot_start_degree;
    const pStop = irrigationSystem.pivot_stop_degree;

    function getRange(start: number, stop: number): number {
        return stop < start ? 360 - start + stop : stop - start;
    }

    let totalDegrees = 0;
    for (let i = 0; i < table.length; i++) {
        const sector = table[i];
        let sectorStart = sector.start;
        let sectorStop = sector.stop;

        if (!isNumber(sectorStart) || !isNumber(sectorStop)) {
            return "Degrees start/stop are required"; // Missing start/stop
        }
        sectorStart = Number(sectorStart);
        sectorStop = Number(sectorStop);
        const lastStop = isNumber(table[i - 1]?.stop)
            ? Number(table[i - 1]?.stop)
            : 0;
        if (sectorStart < lastStop) {
            return "Degrees start/stop are overlapping"; // Overlapping detected
        }
        totalDegrees = totalDegrees + getRange(sectorStart, sectorStop);

        if (
            i > 0 &&
            sectorStart !== lastStop &&
            !(lastStop === pStop && sectorStart === pStart)
        ) {
            return "Degrees are not continuous"; // Discontinuity detected
        }
    }
    const pivotRange = getRange(pStart, pStop);
    if (Math.abs(totalDegrees - pivotRange) > 1) {
        return `Degrees do not match irrigation system start/stop of ${pStart}/${pStop}`; // Total degrees do not sum to total (allowing 1° tolerance for odd start/stop pivots)
    }

    return undefined;
}

export type OperationFormType = InferType<typeof operationFormSchema>;

export function autoPopulateTable(
    table?: {
        target_rate_n?: number;
        product_rate?: number;
        speed?: number;
        depth?: number;
    }[],
    selectedProduct?: FertilizerProduct,
    irrigationSystem?: IrrigationSystem,
) {
    if (!table) return [];
    return table.map((feature) => {
        if (!selectedProduct) return feature;

        let formattedFeature = {
            ...feature,
        };

        if (feature.target_rate_n) {
            formattedFeature = {
                ...formattedFeature,
                product_rate: targetRateNToProdutcRate(
                    feature.target_rate_n,
                    selectedProduct,
                ),
            };
        }

        if (feature.speed) {
            formattedFeature = {
                ...formattedFeature,
                depth: speedToDepth(feature.speed, irrigationSystem),
            };
        }

        if (!feature.target_rate_n && feature.product_rate) {
            formattedFeature = {
                ...formattedFeature,
                target_rate_n: productRateToTargetRateN(
                    feature.product_rate,
                    selectedProduct,
                ),
            };
        }
        if (!feature.speed && feature.depth) {
            formattedFeature = {
                ...formattedFeature,
                speed: depthToSpeed(feature.depth, irrigationSystem),
            };
        }
        return formattedFeature;
    });
}
